/**
 * `on_spell_stat_calc` — how fast a spell actually goes off, and what it costs.
 *
 * The game runs a whole second stat event to decide this: `SpellStatsCalculationEvent` seeds
 * the spell's declared numbers, sweeps the caster's stats over them, then clamps. It is the
 * same machinery as the damage event over a different set of numbers, which is why almost
 * everything here is reused rather than rewritten — `evaluateIfs`, `applyStatEffect` and the
 * `spell_has_tag_*` conditions all work unchanged.
 *
 * ## The rate, as 6.4.13 computes it
 *
 * This is the part that changed most between the reference checkout (6.4.8) and the jar this
 * pack runs (6.4.13), and the change is not cosmetic — it decides the denominator of every DPS
 * figure. 6.4.13 added `cast_speed_ticks` to `SpellConfiguration` and made it, not
 * `cooldown_ticks`, the number that governs how often most spells go off:
 *
 *   - `SpellStatsCalculationEvent` seeds `CAST_SPEED_TICKS`, `CAST_SPEED_PERCENT` and
 *     `CHANNEL_SPEED_PERCENT` alongside the numbers 6.4.8 already had. All 40 of the pack's
 *     `*_cast_time` stats plus `skill_speed` write `cast_speed_perc`;
 *   - `activate()` turns that percentage into `speedMulti = 1 + max(-99, percent) / 100`, then
 *     sets `CAST_SPEED_TICKS = max(GLOBAL_COOLDOWN_TICKS, cast_speed_ticks / speedMulti)` and
 *     divides `CAST_TICKS` by the same multiplier. A `channel` spell folds
 *     `channel_speed_perc` in first, weighted by `CHANNEL_GENERAL_SPEED_TRANSFER`;
 *   - `Spell.getEffectiveCooldownTicks` is `max(getCooldownTicks, getCastSpeedTicks)`, so a
 *     spell's own cooldown can never be shorter than its cast speed;
 *   - `SpellCastingData.armGlobalCooldown` puts `getCastSpeedTicks` on the **shared**
 *     `GLOBAL_COOLDOWN` key on every cast, unless `cast_speed_ticks <= 0`
 *     (`SpellConfiguration.isOffGlobalCooldown`). That shared arm is what makes a rotation cost
 *     real time, and it is what `fullDps` divides by.
 *
 * Without this, a spell like `raging_dragon` — which declares `cast_time_ticks: 0` and
 * `cooldown_ticks: 0` and carries all of its rate in `cast_speed_ticks: 15` — resolves to a
 * one-tick cycle and reports a DPS twenty times too large.
 *
 * `times_to_cast` still fires that many times *during* the cast (`Spell.onCastingTick`,
 * Spell.java:169-183) rather than as a burst afterwards.
 *
 * ## The rate is portable; the target count is not
 *
 * What the mod does not answer is how many times one *target* is hit by a spell that fires
 * nine projectiles in a nova. That is geometry — see `geometry.ts`, which integrates the
 * projectile's own flight from the same fields the game reads, and `skill-model.ts`, which
 * decides which damage sources a cast produces in the first place.
 */
import { CATEGORY, activeSupportLinks, entry } from "@cte2/schema";
import { levelScalingMulti } from "../balance.js";
import { supportSocketsFor } from "../collect/spell.js";
import { evaluateIfs } from "./conditions.js";
import { applyStatEffect } from "./effects.js";
import { DamageEventState, EVENT } from "./event.js";
import { UNKNOWN_ORDER_PRIORITY, datapackPriority } from "./priority.js";
/** `SpellStatsCalculationEvent.ID`. */
const ON_SPELL_STAT_CALC = "on_spell_stat_calc";
/** Minecraft's tick rate. Every duration in a spell config is in ticks. */
export const TICKS_PER_SECOND = 20;
/**
 * `SpellConfiguration.DEFAULT_PROC_COOLDOWN_TICKS`.
 *
 * 429 of the pack's 432 spells declare a `proc_cooldown_ticks` of their own; this is what the
 * other three get. It is the only thing limiting how often a spell that cannot be cast happens.
 */
export const DEFAULT_PROC_COOLDOWN_TICKS = 20;
/**
 * Reads a spell's declared configuration.
 *
 * `setChargesAndRegen` force-overwrites `cooldown_ticks = 3` for every charge-based spell
 * (SpellConfiguration.java:81-87), so a charge spell's real rate lives in `charge_regen` and
 * the cooldown is a formality. `rateOf` handles that; this only reports what is declared.
 */
export function spellConfig(spell) {
    const config = asObject(spell["config"]) ?? {};
    const acts = findActs(spell);
    return {
        castTimeTicks: num(config["cast_time_ticks"]) ?? 0,
        isChannel: config["channel_skill"] === true,
        castSpeedTicks: num(config["cast_speed_ticks"]) ?? 0,
        procCooldownTicks: num(config["proc_cooldown_ticks"]) ?? 0,
        cooldownTicks: num(config["cooldown_ticks"]) ?? 20,
        chargeRegenTicks: num(config["charge_regen"]) ?? 0,
        charges: num(config["charges"]) ?? 0,
        timesToCast: Math.max(1, num(config["times_to_cast"]) ?? 1),
        manaCost: leveled(config["mana_cost"]),
        energyCost: leveled(config["ene_cost"]),
        tags: readTags(config["tags"]),
        projectileCount: acts.projectileCount,
        chainCount: acts.chainCount,
    };
}
/**
 * The `on_spell_stat_calc` sweep — `SpellStatsCalculationEvent` (:33-84).
 *
 * All 105 blocks in this pack sit at `data_modification` (priority 0) on the Source side and
 * are gated almost entirely on `spell_has_tag_*`, which `evaluateIfs` already answers. Only the
 * numbers are new.
 */
export function calculateSpell(input) {
    const declared = spellConfig(input.spell);
    const event = new DamageEventState(input.layers);
    // `manamultilvl` — two factors, both applied to mana and energy alike
    // (SpellStatsCalculationEvent.java:50-59):
    //
    //     float manamultilvl = GameBalanceConfig.get().MANA_COST_SCALING.getMultiFor(lvl);
    //     var gem = Load.player(p).getSkillGemInventory().getSpellGem(spell);
    //     if (gem != null) { manamultilvl *= gem.getManaCostMulti(); }
    //
    // The second was missing for as long as the document could not say which support gems were
    // linked. It can now, and it is not a rounding error: most support gems charge 1.2x or 1.3x
    // and they compound, so five of them roughly doubles what a cast costs.
    const costMulti = levelScalingMulti(input.balance.manaCostScaling, input.characterLevel, input.balance.maxLevel) *
        supportCostMulti(input.snapshot, input.skill, input.equipped ?? []);
    event.data.setupNumber(EVENT.CAST_TICKS, declared.castTimeTicks);
    event.data.setupNumber(EVENT.CAST_SPEED_TICKS, declared.castSpeedTicks);
    // Both seeded at 0 by `SpellStatsCalculationEvent`, which is what makes `add_to_number`
    // meaningful for the 40 stats that write them.
    event.data.setupNumber(EVENT.CAST_SPEED_PERCENT, 0);
    event.data.setupNumber(EVENT.CHANNEL_SPEED_PERCENT, 0);
    event.data.setupNumber(EVENT.COOLDOWN_TICKS, declared.cooldownTicks);
    event.data.setupNumber(EVENT.CHARGE_COOLDOWN_TICKS, declared.chargeRegenTicks);
    event.data.setupNumber(EVENT.MANA_COST, costMulti * declared.manaCost);
    event.data.setupNumber(EVENT.ENERGY_COST, costMulti * declared.energyCost);
    event.data.setupNumber(EVENT.PROJECTILE_SPEED_MULTI, 1);
    event.data.setupNumber(EVENT.PROJECTILE_YAW_SPEED_MULTI, 1);
    event.data.setupNumber(EVENT.PROJECTILE_SPREAD_RANDOMNESS, 1);
    event.data.setupNumber(EVENT.DURATION_MULTI, 1);
    event.data.setupNumber(EVENT.AREA_MULTI, 1);
    event.data.setupNumber(EVENT.AGGRO_RADIUS_MULTI, 1);
    // Not seeded by the game — the effects that write them use `add_to_number`, which reads a
    // number that starts at 0. Seeding them keeps `getOriginalNumber` defined for both.
    event.data.setupNumber(EVENT.BONUS_PROJECTILES, 0);
    event.data.setupNumber(EVENT.BONUS_CHAINS, 0);
    // The summon limits, the same way: `getMaxSummons` reads `getNumber(key, 0)` and floors the
    // result at 1, so a character with no `max_totems` stat keeps exactly one totem alive.
    event.data.setupNumber(EVENT.MAX_TOTEMS, 0);
    event.data.setupNumber(EVENT.MAX_BANNERS, 0);
    event.data.setupNumber(EVENT.EXTRA_TOTEMS, 0);
    event.data.setupNumber(EVENT.EXTRA_BANNERS, 0);
    event.data.setupNumber(EVENT.BONUS_TOTAL_SUMMONS, 0);
    event.data.setString(EVENT.SPELL, input.skill.spellId);
    event.data.setString(EVENT.ELEMENT, "Physical");
    // `this.data.setString(EventData.SUMMON_TYPE, spell.config.summonType.id)`
    // — SpellStatsCalculationEvent.java:45. The snapshot writes the enum constant (`"SPIDER"`) and
    // the five `summon_type_is_*` conditions compare against `SummonType.id`, which is its lower
    // case. Without this every `max_<type>_summons` gate failed and every summon cap read zero.
    event.data.setString(EVENT.SUMMON_TYPE, summonTypeOf(input.spell));
    // `this.data.setString(EventData.STYLE, spell.config.getStyle().id)` — :43. Left unset, the
    // `style_is_int_is_false` gate on `attack_cast_speed` passed for every spell, so Attack Skill
    // Speed — Sniper's -50 — paced Armageddon, an `int` channel it never touches in game.
    const style = styleOf(input.spell);
    if (style.length > 0)
        event.data.setString(EVENT.STYLE, style);
    const ctx = {
        snapshot: input.snapshot,
        index: input.index,
        balance: input.balance,
        // Nothing in this event reads a compat field — they all describe damage — but the context
        // type wants one, so it takes the caller's rather than inventing a shape.
        compat: input.compat,
        event,
        source: input.sheet,
        target: new Map(),
        sourceLevel: input.characterLevel,
        targetLevel: input.characterLevel,
        spell: input.spell,
        spellId: input.skill.spellId,
        spellTags: new Set(declared.tags),
        config: input.config,
        effects: input.effects,
        diagnostics: input.diagnostics,
        report: (severity, code, path, message) => input.diagnostics.push({ severity, code, path, message }),
        reportedConditions: new Set(),
        reportedEffects: new Set(),
        pinnedBooleans: new Set(),
        disableSourceStats: false,
        sourceIsTarget: false,
    };
    const queue = [];
    for (const [statId, stat] of input.sheet) {
        const def = input.index.get(statId);
        for (const block of def?.effects ?? []) {
            // Source only: `SpellStatsCalculationEvent` passes the caster as both sides, and every
            // one of the pack's 105 blocks declares Source.
            if (block.side !== "Source")
                continue;
            if (!block.events.includes(ON_SPELL_STAT_CALC))
                continue;
            if (stat.value === 0)
                continue;
            queue.push({
                priority: datapackPriority(block.order) ?? UNKNOWN_ORDER_PRIORITY,
                statId,
                run: () => {
                    const weight = evaluateIfs(ctx, block.ifs, { statId, value: stat.value, element: def?.element }, "Source");
                    if (weight <= 0)
                        return;
                    const shape = input.index.shapeOf(statId);
                    for (const effectId of block.effects) {
                        applyStatEffect(ctx, effectId, { statId, value: stat.value, dmgMulti: stat.dmgMulti, multiUseType: shape.multiUseType }, "Source", weight);
                    }
                },
            });
        }
    }
    queue.sort((a, b) => (a.priority === b.priority ? a.statId.localeCompare(b.statId) : a.priority - b.priority));
    for (const item of queue)
        item.run();
    // The tag here, deliberately: `channel_speed_perc` is gated by
    // `StatConditions.SPELL_HAS_TAG.get(SpellTags.channel)`, not by the config flag.
    return clampSpellCalc(event, declared, input.balance, declared.tags.includes("channel"));
}
/** `spell.config.summonType.id` — `SummonType.NONE.id` for the 411 spells that summon nothing. */
function summonTypeOf(spell) {
    const config = asObject(spell["config"]);
    const declared = config?.["summonType"];
    return typeof declared === "string" && declared.length > 0 ? declared.toLowerCase() : "none";
}
/** `spell.config.getStyle().id` — `PlayStyle.id`, the lower case every `style` match compares. */
function styleOf(spell) {
    const config = asObject(spell["config"]);
    const style = config?.["style"];
    return typeof style === "string" ? style.toLowerCase() : "";
}
/**
 * `SpellStatsCalculationEvent.activate` and `Spell.getCastTimeTicks` (:205-208), at 6.4.13.
 *
 *     int cd = (int) Mth.clamp(data.getNumber(COOLDOWN_TICKS).number,
 *             config.cooldown_ticks * MIN_SPELL_COOLDOWN_MULTI, 1000000);
 *     ... same for CHARGE_COOLDOWN_TICKS against charge_regen ...
 *
 *     float percent = data.getNumber(CAST_SPEED_PERCENT).number;
 *     if (spell.is(SpellTags.channel)) {
 *         percent = percent * CHANNEL_GENERAL_SPEED_TRANSFER
 *                 + data.getNumber(CHANNEL_SPEED_PERCENT).number;
 *     }
 *     float multi = 1 + Math.max(-99f, percent) / 100f;
 *
 *     data.getNumber(CAST_SPEED_TICKS).number =
 *             Math.max(GLOBAL_COOLDOWN_TICKS, data.getNumber(CAST_SPEED_TICKS).number / multi);
 *     data.getNumber(CAST_TICKS).number = data.getNumber(CAST_TICKS).number / multi;
 *
 * — read out of `Mine_and_Slash-1.20.1-6.4.13.jar`, which is the version this snapshot was
 * extracted from; `reference/mns-src` is 6.4.8 and has none of it.
 *
 * The cast-time floor is `times_to_cast`, not 0 — a spell that casts five times over its
 * duration needs at least five ticks to do it in.
 */
function clampSpellCalc(event, declared, balance, channel) {
    const floorFor = (base) => base * balance.minSpellCooldownMulti;
    const rawPercent = event.data.getNumber(EVENT.CAST_SPEED_PERCENT);
    const percent = channel
        ? rawPercent * balance.channelSpeedTransfer + event.data.getNumber(EVENT.CHANNEL_SPEED_PERCENT)
        : rawPercent;
    // The -99 floor is the game's, and it is what stops a slow enough build from dividing by zero
    // or turning the cast around: at -99% a cast takes 100x as long, never negative time.
    const speedMulti = 1 + Math.max(-99, percent) / 100;
    const cooldownTicks = Math.trunc(clamp(event.data.getNumber(EVENT.COOLDOWN_TICKS), floorFor(declared.cooldownTicks), 1_000_000));
    const castTicksBeforeSpeed = event.data.getNumber(EVENT.CAST_TICKS);
    const castSpeedTicksBeforeSpeed = event.data.getNumber(EVENT.CAST_SPEED_TICKS);
    return {
        ...speedTicks(castTicksBeforeSpeed, castSpeedTicksBeforeSpeed, speedMulti, cooldownTicks, declared, balance),
        castTicksBeforeSpeed,
        castSpeedTicksBeforeSpeed,
        castSpeedPercent: percent,
        speedMulti,
        cooldownTicks,
        chargeCooldownTicks: Math.trunc(clamp(event.data.getNumber(EVENT.CHARGE_COOLDOWN_TICKS), floorFor(declared.chargeRegenTicks), 1_000_000)),
        manaCost: Math.max(0, event.data.getNumber(EVENT.MANA_COST)),
        energyCost: Math.max(0, event.data.getNumber(EVENT.ENERGY_COST)),
        bonusProjectiles: Math.trunc(event.data.getNumber(EVENT.BONUS_PROJECTILES)),
        bonusChains: Math.trunc(event.data.getNumber(EVENT.BONUS_CHAINS)),
        areaMulti: event.data.getNumber(EVENT.AREA_MULTI),
        durationMulti: event.data.getNumber(EVENT.DURATION_MULTI),
        projectileSpeedMulti: event.data.getNumber(EVENT.PROJECTILE_SPEED_MULTI, 1),
        projectileYawSpeedMulti: event.data.getNumber(EVENT.PROJECTILE_YAW_SPEED_MULTI, 1),
        projectileSpreadMulti: event.data.getNumber(EVENT.PROJECTILE_SPREAD_RANDOMNESS, 1),
        pierce: event.data.getBoolean(EVENT.PIERCE),
        nova: event.data.getBoolean(EVENT.NOVA),
        barrage: event.data.getBoolean(EVENT.BARRAGE),
        // `getMaxSummons` floors at 1, so the floor belongs here rather than at the use site — a
        // build with no `max_totems` still gets one totem.
        maxTotems: Math.max(1, Math.trunc(event.data.getNumber(EVENT.MAX_TOTEMS))),
        maxBanners: Math.max(1, Math.trunc(event.data.getNumber(EVENT.MAX_BANNERS))),
        extraTotems: Math.trunc(event.data.getNumber(EVENT.EXTRA_TOTEMS)),
        extraBanners: Math.trunc(event.data.getNumber(EVENT.EXTRA_BANNERS)),
        bonusTotalSummons: Math.trunc(event.data.getNumber(EVENT.BONUS_TOTAL_SUMMONS)),
        summonType: event.data.getString(EVENT.SUMMON_TYPE) || "none",
    };
}
/**
 * The tick counts the speed multiplier decides, each a whole tick.
 *
 * `Spell.getCastTimeTicks` and `getCastSpeedTicks` both return `(int) Math.ceil(number)` in the
 * 6.4.13 jar, so skill speed moves nothing until it carries a count past a tick boundary —
 * which is what {@link nextSpeedStep} searches for.
 */
function speedTicks(castTicksBeforeSpeed, castSpeedTicksBeforeSpeed, speedMulti, cooldownTicks, declared, balance) {
    const castSpeedTicks = Math.ceil(Math.max(balance.globalCooldownTicks, castSpeedTicksBeforeSpeed / speedMulti));
    // `Spell.getCastTimeTicks` reads the number the event divided, then clamps and ceils it.
    const castTicks = clamp(Math.ceil(castTicksBeforeSpeed / speedMulti), declared.timesToCast, 10000);
    // `SpellConfiguration.isOffGlobalCooldown()` — the *declared* field, not the calculated one,
    // which matters because the calculated one is floored at the GCD and so is never 0.
    const offGlobalCooldown = declared.castSpeedTicks <= 0;
    return {
        castTicks,
        castSpeedTicks: offGlobalCooldown ? 0 : castSpeedTicks,
        // `Spell.getEffectiveCooldownTicks` — `Math.max(getCooldownTicks(ctx), getCastSpeedTicks(ctx))`.
        effectiveCooldownTicks: offGlobalCooldown ? cooldownTicks : Math.max(cooldownTicks, castSpeedTicks),
        offGlobalCooldown,
    };
}
/**
 * One cast cycle, in seconds.
 *
 * `onSpellCastFinished` calls `setCooldownOnCasted`, so recovery starts when the cast
 * *finishes* rather than alongside it — the two are sequential. What recovery is has changed
 * since 6.4.8: `setCooldownOnCasted` now puts `Spell.getEffectiveCooldownTicks` on the spell,
 * which is `max(cooldown_ticks, cast_speed_ticks)`, so a spell declaring no cooldown at all is
 * still limited by its cast speed.
 *
 * A charge spell is the exception. `setChargesAndRegen` force-overwrites `cooldown_ticks = 3`
 * when charges are declared (SpellConfiguration.java:81-87), so the number that governs how
 * often it can be cast is the charge regeneration, not the cooldown — but the global cooldown
 * still applies underneath it.
 *
 * A **channel** is the other exception, and it is a different shape rather than a different
 * number. `SpellCastingData.onTimePass` runs `tryChannelPulse` when `castTickLeft` reaches
 * zero, and that method casts the spell and immediately re-arms
 * `castTickLeft = getCastTimeTicks(ctx)`. It never goes through `tryCast` or
 * `onSpellCastFinished`, so no cooldown is set and no global cooldown is armed between pulses:
 * a held channel repeats every `CAST_TICKS` and nothing else. Holding it is what the player
 * does, so that is what is reported — `whirlwind` recovering for its cooldown between swings is
 * a cycle the game never makes you pay.
 */
export function rateOf(calc, declared) {
    // The flag here, not the tag: whether a spell pulses while held is `config.isChannel()`.
    const channelled = declared.isChannel;
    const chargeBased = !channelled && declared.charges > 0 && calc.chargeCooldownTicks > 0;
    const recovery = channelled
        ? 0
        : chargeBased
            ? Math.max(calc.chargeCooldownTicks, calc.castSpeedTicks)
            : calc.effectiveCooldownTicks;
    // A spell with no cast speed and no cooldown of its own has no cycle to compute, and the
    // floor below would give it one tick — 20 casts a second, which is how `cursed_raging_dragon`
    // came to report 27 million DPS and take eight of the pack's top ten. It is not castable at
    // all; the only thing that limits how often it happens is `proc_cooldown_ticks`, the same
    // field `procs.ts` caps a proc's rate with. Using it here makes the standalone figure a
    // stated ceiling instead of an artefact of the floor.
    const castable = !calc.offGlobalCooldown;
    const procPaced = !castable && !channelled && recovery <= 0;
    const procCeiling = declared.procCooldownTicks > 0 ? declared.procCooldownTicks : DEFAULT_PROC_COOLDOWN_TICKS;
    // A channel pulse is one `CAST_TICKS`; anything else casts and then recovers, in that order.
    // A proc-paced spell is neither: it is not cast, so its cast time is not *added* to a recovery
    // it does not have. What limits it is `is_<spell>_not_on_cd`, which `procs.ts` reads as a cap of
    // `20 / proc_cooldown_ticks` per second — a repeat interval, measured from the last time it
    // fired. The cast time can only bind when it is the longer of the two.
    const ticks = procPaced
        ? Math.max(calc.castTicks, procCeiling)
        : Math.max(channelled ? 1 : 0, calc.castTicks + recovery);
    return {
        castable,
        procPaced,
        cycleSeconds: ticks / TICKS_PER_SECOND,
        castSeconds: calc.castTicks / TICKS_PER_SECOND,
        cooldownSeconds: (procPaced ? procCeiling : recovery) / TICKS_PER_SECOND,
        castsPerCycle: declared.timesToCast,
        chargeBased,
        castSpeedBound: !channelled && !calc.offGlobalCooldown && calc.castSpeedTicks >= calc.cooldownTicks,
        channelled,
        globalCooldownSeconds: channelled ? 0 : calc.castSpeedTicks / TICKS_PER_SECOND,
    };
}
/**
 * The next skill speed threshold, or `undefined` when skill speed cannot shorten this cycle.
 *
 * The cast and the global-cooldown arm are both ceiled to whole ticks, so between thresholds
 * extra speed changes nothing. Each count `ceil(X / multi)` drops to `k` once
 * `multi >= X / k`; those are the only multipliers worth trying, and the first one whose
 * {@link rateOf} cycle is shorter is the answer. A threshold can move nothing — the global
 * cooldown dropping a tick under a longer cooldown — which is why each is checked against the
 * whole cycle rather than taken as found.
 *
 * `undefined` when the spell is `not_affected_by_cast_speed`, is proc-paced, or has a cycle no
 * amount of speed shortens (a cooldown that outlasts the cast and global cooldown at their
 * floors).
 */
export function nextSpeedStep(calc, declared, balance) {
    if (declared.tags.includes("not_affected_by_cast_speed"))
        return undefined;
    const now = rateOf(calc, declared);
    if (now.procPaced || (!now.castable && !now.channelled))
        return undefined;
    const cycleAt = (multi) => {
        const ticks = speedTicks(calc.castTicksBeforeSpeed, calc.castSpeedTicksBeforeSpeed, multi, calc.cooldownTicks, declared, balance);
        return Math.round(rateOf({ ...calc, ...ticks, speedMulti: multi }, declared).cycleSeconds * TICKS_PER_SECOND);
    };
    const current = cycleAt(calc.speedMulti);
    const candidates = [];
    const thresholds = (beforeSpeed, floor) => {
        if (beforeSpeed <= 0)
            return;
        const top = Math.ceil(beforeSpeed / calc.speedMulti) - 1;
        for (let k = top; k >= Math.max(1, floor) && top - k < 200; k--)
            candidates.push(beforeSpeed / k);
    };
    thresholds(calc.castTicksBeforeSpeed, declared.timesToCast);
    if (!calc.offGlobalCooldown)
        thresholds(calc.castSpeedTicksBeforeSpeed, Math.ceil(balance.globalCooldownTicks));
    candidates.sort((a, b) => a - b);
    // A channel's multiplier moves by `transfer` per point of cast speed — see `clampSpellCalc`.
    const transfer = declared.tags.includes("channel") ? balance.channelSpeedTransfer : 1;
    if (transfer <= 0)
        return undefined;
    for (const multi of candidates) {
        if (multi <= calc.speedMulti)
            continue;
        // Rounded up to the tenth a tooltip shows, then re-checked: the game divides in float, so
        // landing exactly on a boundary is not a promise of crossing it.
        let more = Math.ceil((((multi - calc.speedMulti) * 100) / transfer) * 10 - 1e-6) / 10;
        let next = cycleAt(calc.speedMulti + (more * transfer) / 100);
        if (next >= current) {
            more = Math.round((more + 0.1) * 10) / 10;
            next = cycleAt(calc.speedMulti + (more * transfer) / 100);
        }
        if (next < current)
            return { morePercent: more, cycleTicks: current, nextCycleTicks: next };
    }
    return undefined;
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
}
/** `LevelScalingConfig.getMultiFor`, for the one curve that is not a `StatScaling`. */
/**
 * `SocketedGem.getManaCostMulti` (SocketedGem.java:132-142).
 *
 *     float multi = 1;
 *     for (SkillGemData data : this.getSupportDatas()) { multi *= data.getSupport().manaMulti; }
 *     return multi;
 *
 * A product, not a sum, and read off the support gem's registry entry rather than off the
 * linked item — so it is the same for every copy of a gem however it rolled. `manaMulti`
 * defaults to 0.25 in the Java, which no shipped gem uses; an entry that is somehow missing the
 * field is left at 1 rather than given a discount the game does not grant.
 */
function supportCostMulti(snapshot, skill, equipped) {
    // A borrowing spell is costed off the sockets it actually uses, which are the lender's —
    // `MercenaryData` picks the slot the same way (`usesSupportGemsFromAnotherSpell() ?
    // getSpellUsedForSuppGems().GUID() : ...`). No lender on the bar means no sockets at all.
    const sockets = supportSocketsFor(snapshot, skill, equipped);
    if (sockets === undefined)
        return 1;
    let multi = 1;
    for (const link of activeSupportLinks(sockets.skill)) {
        const data = entry(snapshot, CATEGORY.supportGem, link.id)?.data;
        const declared = data === undefined ? undefined : num(data["manaMulti"]);
        if (declared !== undefined)
            multi *= declared;
    }
    return multi;
}
function num(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}
/** A `LeveledValue` in a config block. Only the base matters for a cost. */
function leveled(value) {
    if (typeof value === "number")
        return value;
    const obj = asObject(value);
    if (!obj)
        return 0;
    return num(obj["base"]) ?? num(obj["min"]) ?? 0;
}
/** `TagList<SpellTag>` serialises as `{ tags: [...] }`, not as a bare array. */
function readTags(value) {
    const list = asObject(value)?.["tags"] ?? value;
    return Array.isArray(list) ? list.filter((t) => typeof t === "string") : [];
}
function asObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value)
        ? value
        : undefined;
}
/**
 * The first `projectile` act's `proj_count`, and the first `chain_count` anywhere.
 *
 * Both live in the `attached` component tree rather than in `config`, alongside the `damage`
 * act `simulateHit` already walks for. `skill-model.ts` enumerates every one of them; this
 * stays as the headline pair the spell unit reports.
 */
function findActs(spell) {
    let projectileCount = 0;
    let chainCount = 0;
    const visit = (node) => {
        if (Array.isArray(node)) {
            for (const item of node)
                visit(item);
            return;
        }
        const obj = asObject(node);
        if (!obj)
            return;
        const map = asObject(obj["map"]) ?? obj;
        if (projectileCount === 0)
            projectileCount = num(map["proj_count"]) ?? 0;
        if (chainCount === 0)
            chainCount = num(map["chain_count"]) ?? 0;
        for (const value of Object.values(obj))
            visit(value);
    };
    visit(spell["attached"]);
    return { projectileCount, chainCount };
}
//# sourceMappingURL=spell-calc.js.map